import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContractsService, formatB2bDevolucaoReturnDateUtc } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import {
  B2B_DESVINCULAR_CONDUTOR_MOTIVO_OPTIONS,
  B2B_DESVINCULAR_OUTRO_MIN_LENGTH,
  B2bDesvincularCondutorMotivo,
  resolveDesvincularMotivoPayload
} from '../../../models/b2b-desvincular-condutor-motivo';

@Component({
  selector: 'app-b2b-contracts-driver-assignment',
  templateUrl: './b2b-contracts-driver-assignment.component.html',
  styleUrls: ['./b2b-contracts-driver-assignment.component.css']
})
export class B2bContractsDriverAssignmentComponent implements OnInit {
  contractId: number | null = null;
  contract: any = null;
  drivers: any[] = [];
  modalDrivers: any[] = [];
  itemsTotal = 0;
  vehicles: any[] = [];
  filteredVehicles: any[] = [];
  filteredDrivers: any[] = [];
  
  isLoading = false;
  isLoadingDrivers = false;
  isLoadingItems = false;
  
  driverPageNumber = 1;
  driverPageSize = 10;
  driverTotalItems = 0;
  driverTotalPages = 1;
  driverHasNextPage = false;
  driverHasPreviousPage = false;

  // Paginação de veículos
  vehiclePageNumber = 1;
  vehiclePageSize = 10;
  vehicleTotalItems = 0;
  vehicleTotalPages = 1;
  vehicleHasNextPage = false;
  vehicleHasPreviousPage = false;
  assignedVehiclesCount = 0;
  availableVehiclesCount = 0;

  private driverSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private vehicleSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private modalDriverSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private driverCache: any[] = [];
  private localDriverPaging = false;
  private vehicleStatsFallbackDone = false;
  
  // Filtros e busca
  vehicleFilter: 'all' | 'available' | 'assigned' = 'all';
  driverSearchTerm: string = '';
  vehicleSearchTerm: string = '';
  modalDriverSearchTerm: string = '';
  isLoadingModalDrivers = false;
  
  // Modal de vinculação
  showAssignmentModal = false;
  assignmentForm: FormGroup;
  selectedVehicle: any = null;
  isSubmitting = false;
  
  // Modal de visualização
  showViewModal = false;
  assignmentDetails: any = null;
  isLoadingAssignmentDetails = false;

  /** Desvincular condutor — PUT body { motivo } */
  showUnassignModal = false;
  showUnassignOutroModal = false;
  vehicleToUnassign: any = null;
  unassignForm: FormGroup;
  unassignOutroForm: FormGroup;
  readonly desvincularMotivoOptions = B2B_DESVINCULAR_CONDUTOR_MOTIVO_OPTIONS;
  readonly desvincularOutroMinLength = B2B_DESVINCULAR_OUTRO_MIN_LENGTH;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private fb: FormBuilder
  ) {
    this.assignmentForm = this.fb.group({
      driverId: ['', [Validators.required]],
      startDate: ['', [Validators.required]],
      endDate: [null],
      status: ['Active', [Validators.required]],
      notes: ['']
    });
    this.unassignForm = this.fb.group({
      motivo: ['', [Validators.required]],
      returnDate: [null as string | null]
    });
    this.unassignOutroForm = this.fb.group({
      detalhe: [
        '',
        [Validators.required, Validators.minLength(B2B_DESVINCULAR_OUTRO_MIN_LENGTH)]
      ]
    });
  }

  /** Controle do carregamento inicial da página (contrato + listas). */
  private initialLoadPending = 0;
  private initialLoadActive = false;

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.beginInitialPageLoad(3);
        this.loadContract();
        this.loadDrivers();
        this.loadItems();
        this.loadVehicles();
      }
    });
  }

  private beginInitialPageLoad(parts: number): void {
    this.initialLoadActive = true;
    this.initialLoadPending = parts;
    this.isLoading = true;
  }

  private markInitialLoadPartDone(): void {
    if (!this.initialLoadActive) {
      return;
    }
    this.initialLoadPending = Math.max(0, this.initialLoadPending - 1);
    if (this.initialLoadPending === 0) {
      this.initialLoadActive = false;
      this.isLoading = false;
    }
  }

  loadContract(): void {
    if (!this.contractId) return;

    if (!this.initialLoadActive) {
      this.isLoading = true;
    }
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        this.contract = contract;
        if (this.initialLoadActive) {
          this.markInitialLoadPartDone();
        } else {
          this.isLoading = false;
        }
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        if (this.initialLoadActive) {
          this.markInitialLoadPartDone();
        } else {
          this.isLoading = false;
        }
      }
    });
  }

  loadDrivers(): void {
    if (!this.contractId) return;

    if (!this.initialLoadActive) {
      this.isLoadingDrivers = true;
    }
    const searching = !!(this.driverSearchTerm || '').trim();
    this.localDriverPaging = searching;
    const requestPage = searching ? 1 : this.driverPageNumber;
    const requestSize = searching ? 200 : this.driverPageSize;

    this.contractsService.getB2bContractDrivers(this.contractId, requestPage, requestSize, this.driverSearchTerm).subscribe({
      next: (response) => {
        const driversData = Array.isArray(response?.data) ? response.data : [];
        let mapped = driversData.map((driver: any) => this.mapDriver(driver));
        if (searching) {
          mapped = mapped.filter((driver: any) => this.matchesDriverSearch(driver, this.driverSearchTerm));
          this.driverCache = mapped;
          this.driverTotalItems = mapped.length;
          this.applyLocalDriverPage();
        } else {
          this.driverCache = [];
          this.drivers = mapped;
          const pag = response.pagination || {};
          this.driverTotalItems = pag.totalItems || pag.TotalItems || this.drivers.length;
          this.driverTotalPages = pag.totalPages || pag.TotalPages || Math.max(1, Math.ceil(this.driverTotalItems / this.driverPageSize));
          this.driverPageNumber = pag.currentPage || pag.CurrentPage || this.driverPageNumber;
          this.driverHasPreviousPage = this.driverPageNumber > 1;
          this.driverHasNextPage = this.driverPageNumber < this.driverTotalPages;
          this.filteredDrivers = this.drivers;
        }
        this.isLoadingDrivers = false;
        if (this.initialLoadActive) {
          this.markInitialLoadPartDone();
        }
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoadingDrivers = false;
        if (this.initialLoadActive) {
          this.markInitialLoadPartDone();
        }
      }
    });
  }

  loadItems(): void {
    if (!this.contractId) return;

    this.contractsService.getB2bContractItems(this.contractId, 1, 1).subscribe({
      next: (response) => {
        const pag = response.pagination || response.Pagination || {};
        this.itemsTotal = pag.totalItems || pag.TotalItems || (Array.isArray(response.data) ? response.data.length : 0);
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
      }
    });
  }

  /** Busca e filtro rodam no servidor: os totais e as páginas vêm da própria API. */
  loadVehicles(): void {
    if (!this.contractId) return;

    if (!this.initialLoadActive) {
      this.isLoadingItems = true;
    }
    const search = (this.vehicleSearchTerm || '').trim() || null;
    const assigned = this.vehicleFilter === 'assigned'
      ? true
      : this.vehicleFilter === 'available' ? false : null;

    this.contractsService
      .listB2bContractVehicles(this.contractId, this.vehiclePageNumber, this.vehiclePageSize, search, assigned)
      .subscribe({
        next: (response) => {
          const mapped = (response.data || []).map((vehicle: any) => this.mapVehicle(vehicle));
          this.vehicles = mapped;
          this.filteredVehicles = mapped;

          const pagination = response.pagination || {};
          this.vehicleTotalItems = pagination.totalItems ?? mapped.length;
          this.vehicleTotalPages = Math.max(1, pagination.totalPages || 1);
          this.vehiclePageNumber = pagination.currentPage || this.vehiclePageNumber;
          this.vehicleHasPreviousPage = this.vehiclePageNumber > 1;
          this.vehicleHasNextPage = this.vehiclePageNumber < this.vehicleTotalPages;

          this.applyVehicleStats(response, mapped);
          this.isLoadingItems = false;
          this.ensureVehicleStatsLoaded(response);
          if (this.initialLoadActive) {
            this.markInitialLoadPartDone();
          }
        },
        error: (err) => {
          this.errorService.handleHttpError(err);
          this.isLoadingItems = false;
          if (this.initialLoadActive) {
            this.markInitialLoadPartDone();
          }
        }
      });
  }

  private applyVehicleStats(response: any, vehicles: any[]): void {
    const assigned = response?.assignedCount;
    const available = response?.availableCount;
    if (assigned != null && available != null) {
      this.assignedVehiclesCount = Number(assigned);
      this.availableVehiclesCount = Number(available);
      return;
    }
    this.assignedVehiclesCount = vehicles.filter((v: any) => v.hasDriver).length;
    this.availableVehiclesCount = vehicles.filter((v: any) => !v.hasDriver).length;
  }

  /** Fallback quando a API não devolve os contadores do contrato inteiro (uma vez por tela). */
  private ensureVehicleStatsLoaded(response: any): void {
    if (response?.assignedCount != null && response?.availableCount != null) {
      return;
    }
    if (!this.contractId || this.vehicleStatsFallbackDone) {
      return;
    }
    this.vehicleStatsFallbackDone = true;
    this.contractsService.listAllB2bContractVehicles(this.contractId, 100).subscribe({
      next: (all) => {
        const mapped = (all || []).map((vehicle: any) => this.mapVehicle(vehicle));
        this.assignedVehiclesCount = mapped.filter((v: any) => v.hasDriver).length;
        this.availableVehiclesCount = mapped.filter((v: any) => !v.hasDriver).length;
      }
    });
  }

  onDriverPageChange(page: number): void {
    this.driverPageNumber = page;
    if (this.localDriverPaging) {
      this.applyLocalDriverPage();
      return;
    }
    this.loadDrivers();
  }

  onDriverPageSizeChange(size: number): void {
    this.driverPageSize = size;
    this.driverPageNumber = 1;
    if (this.localDriverPaging) {
      this.applyLocalDriverPage();
      return;
    }
    this.loadDrivers();
  }

  onVehiclePageChange(page: number): void {
    if (page < 1 || page > this.vehicleTotalPages || this.isLoadingItems) return;
    this.vehiclePageNumber = page;
    this.loadVehicles();
  }

  onVehiclePageSizeChange(size: number): void {
    this.vehiclePageSize = size;
    this.vehiclePageNumber = 1;
    this.loadVehicles();
  }

  applyFilters(): void {
    this.filteredVehicles = this.vehicles;
    this.filteredDrivers = this.drivers;
  }

  private applyLocalDriverPage(): void {
    this.driverTotalItems = this.driverCache.length;
    this.driverTotalPages = Math.max(1, Math.ceil(this.driverTotalItems / Math.max(1, this.driverPageSize)));
    if (this.driverPageNumber > this.driverTotalPages) {
      this.driverPageNumber = this.driverTotalPages;
    }
    const skip = (this.driverPageNumber - 1) * this.driverPageSize;
    this.drivers = this.driverCache.slice(skip, skip + this.driverPageSize);
    this.filteredDrivers = this.drivers;
    this.driverHasPreviousPage = this.driverPageNumber > 1;
    this.driverHasNextPage = this.driverPageNumber < this.driverTotalPages;
  }

  private matchesDriverSearch(driver: any, term: string): boolean {
    const search = (term || '').trim().toLowerCase();
    if (!search) return true;
    const digits = search.replace(/\D/g, '');
    const values = [driver.name, driver.driverCpf, driver.driverCnh, driver.email, driver.phone]
      .map(value => String(value || '').toLowerCase());
    if (values.some(value => value.includes(search))) return true;
    if (digits.length >= 3) {
      return values.some(value => value.replace(/\D/g, '').includes(digits));
    }
    return false;
  }

  onVehicleFilterChange(filter: string): void {
    const next = (filter === 'available' || filter === 'assigned') ? filter : 'all';
    this.vehicleFilter = next;
    this.vehiclePageNumber = 1;
    this.loadVehicles();
  }

  onDriverSearchChange(): void {
    if (this.driverSearchTimer) clearTimeout(this.driverSearchTimer);
    this.driverSearchTimer = setTimeout(() => {
      this.driverPageNumber = 1;
      this.loadDrivers();
    }, 300);
  }

  onVehicleSearchChange(): void {
    if (this.vehicleSearchTimer) clearTimeout(this.vehicleSearchTimer);
    this.vehicleSearchTimer = setTimeout(() => {
      this.vehiclePageNumber = 1;
      this.loadVehicles();
    }, 300);
  }

  getVehicleDriverAssociation(vehicle: any, item: any): any {
    // Verificar se o veículo tem associação com condutor
    const driverAssociations = item.driverAssociations || item.DriverAssociations || [];
    const vehicleId = vehicle.id || vehicle.Id;
    return driverAssociations.find((assoc: any) => {
      const assocVehicleId = assoc.b2BContractItemVehicleSnapshotId || 
                             assoc.B2BContractItemVehicleSnapshotId ||
                             assoc.vehicleSnapshotId ||
                             assoc.VehicleSnapshotId;
      return assocVehicleId === vehicleId;
    }) || null;
  }

  findAssignmentId(vehicle: any): number | null {
    const fromVehicle = vehicle.b2BContractItemDriverId || vehicle.B2BContractItemDriverId;
    if (fromVehicle) return +fromVehicle;
    if (vehicle.driverAssociation) {
      const assignmentId = vehicle.driverAssociation.id || vehicle.driverAssociation.Id;
      if (assignmentId) return +assignmentId;
    }
    return null;
  }

  viewAssignment(vehicle: any): void {
    if (!vehicle.hasDriver || !this.contractId) return;

    // Obter o ID da vinculação (assignmentId)
    // O endpoint é: GET /b2b/contracts/{contractId}/vehicles/vinculacao/{assignmentId}
    // O assignmentId é o campo B2BContractItemDriverId que vem da API de listagem de veículos
    let assignmentId: number | null = null;
    
    
    // 1. Tentar obter do b2BContractItemDriverId (vem diretamente da API de listagem)
    // Este é o ID da vinculação que vem no campo B2BContractItemDriverId
    if (vehicle.b2BContractItemDriverId) {
      assignmentId = +vehicle.b2BContractItemDriverId;
    }
    
    // 2. Tentar obter do driverAssociation
    if (!assignmentId && vehicle.driverAssociation) {
      assignmentId = vehicle.driverAssociation.id || vehicle.driverAssociation.Id;
      if (assignmentId) {
        assignmentId = +assignmentId;
      }
    }
    
    // 3. Tentar buscar usando o método findAssignmentId
    if (!assignmentId) {
      assignmentId = this.findAssignmentId(vehicle);
      if (assignmentId) {
        assignmentId = +assignmentId;
      }
    }

    if (!assignmentId) {
      console.error('Não foi possível encontrar o ID da vinculação. Veículo completo:', JSON.stringify(vehicle, null, 2));
      this.errorService.showErrorMessage('Não foi possível identificar o ID da vinculação. Por favor, recarregue a página.');
      return;
    }


    this.isLoadingAssignmentDetails = true;
    this.showViewModal = true;
    this.assignmentDetails = null;

    // Usar o endpoint correto: GET /b2b/contracts/{contractId}/vehicles/vinculacao/{assignmentId}
    this.contractsService.getDriverVehicleAssignment(this.contractId, assignmentId).subscribe({
      next: (response) => {
        
        // A resposta vem diretamente como objeto (não dentro de data/Data)
        // Baseado no JSON fornecido pelo usuário, a resposta é direta
        let data = response;
        
        // Se vier dentro de data/Data, extrair (fallback)
        if (response && response.data && !response.VehiclePlate) {
          data = response.data;
        } else if (response && response.Data && !response.VehiclePlate) {
          data = response.Data;
        }
        
        // Se for array, pegar o primeiro item (fallback)
        if (Array.isArray(data) && data.length > 0) {
          data = data[0];
        }
        
        
        // Garantir que temos um objeto válido
        if (data && typeof data === 'object' && (data.VehiclePlate || data.vehiclePlate || data.B2BContractItemId)) {
          this.assignmentDetails = data;
          const driverName = this.pickValue(data, 'DriverName', 'driverName');
          const driverId = this.pickValue(data, 'B2BContractDriverId', 'b2BContractDriverId', 'b2bContractDriverId');
          if (driverName || driverId) {
            const idx = this.vehicles.findIndex(v => v.id === vehicle.id);
            if (idx !== -1) {
              this.vehicles[idx] = {
                ...this.vehicles[idx],
                driverName: driverName || this.vehicles[idx].driverName,
                b2BContractDriverId: driverId || this.vehicles[idx].b2BContractDriverId,
                driverAssociation: {
                  ...(this.vehicles[idx].driverAssociation || {}),
                  b2BContractDriverId: driverId || this.vehicles[idx].b2BContractDriverId,
                  driverName: driverName || this.vehicles[idx].driverName
                }
              };
              this.filteredVehicles = this.vehicles;
            }
          }
        } else {
          console.error('Resposta vazia ou inválida:', data);
          this.errorService.showErrorMessage('Não foi possível carregar os detalhes da vinculação.');
          this.closeViewModal();
        }
        this.isLoadingAssignmentDetails = false;
      },
      error: (err) => {
        console.error('Erro ao buscar vinculação:', err);
        this.errorService.handleHttpError(err);
        this.isLoadingAssignmentDetails = false;
        this.closeViewModal();
      }
    });
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.assignmentDetails = null;
    this.isLoadingAssignmentDetails = false;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  }

  openAssignmentModal(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.assignmentForm.reset();
    this.modalDriverSearchTerm = '';
    this.loadModalDrivers();
    
    // Se o veículo já tem condutor atribuído, preencher os dados
    if (vehicle.driverAssociation) {
      const association = vehicle.driverAssociation;
      const startDate = association.startDate || association.StartDate;
      const endDate = association.endDate || association.EndDate;
      const status = association.status || association.Status || 'Active';
      const notes = association.notes || association.Notes || '';
      
      this.assignmentForm.patchValue({
        driverId: association.b2BContractDriverId || association.B2BContractDriverId || '',
        startDate: startDate ? new Date(startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        endDate: endDate ? new Date(endDate).toISOString().split('T')[0] : null,
        status: status,
        notes: notes
      });
    } else {
      this.assignmentForm.patchValue({
        driverId: '',
        startDate: new Date().toISOString().split('T')[0],
        status: 'Active',
        notes: ''
      });
    }
    
    this.showAssignmentModal = true;
  }

  closeAssignmentModal(): void {
    this.showAssignmentModal = false;
    this.selectedVehicle = null;
    this.modalDriverSearchTerm = '';
    this.modalDrivers = [];
    this.isLoadingModalDrivers = false;
    if (this.modalDriverSearchTimer) {
      clearTimeout(this.modalDriverSearchTimer);
      this.modalDriverSearchTimer = null;
    }
    this.assignmentForm.reset();
  }

  onModalDriverSearchChange(): void {
    if (this.modalDriverSearchTimer) clearTimeout(this.modalDriverSearchTimer);
    this.modalDriverSearchTimer = setTimeout(() => {
      this.loadModalDrivers(this.modalDriverSearchTerm);
    }, 300);
  }

  get filteredModalDrivers(): any[] {
    const source = this.modalDrivers.length ? this.modalDrivers : this.drivers;
    const term = (this.modalDriverSearchTerm || '').trim();
    if (!term) return source;
    return source.filter((driver: any) => this.matchesDriverSearch(driver, term));
  }

  isDriverAlreadyAssigned(driverId: number): { assigned: boolean; vehiclePlate?: string } {
    // Verificar se o condutor já está vinculado a algum veículo
    for (const vehicle of this.vehicles) {
      // Usar HasDriver para verificar se o veículo tem condutor
      if (vehicle.hasDriver) {
        // Se o veículo tem condutor, verificar se é o mesmo condutor
        if (vehicle.driverAssociation) {
          const associationDriverId = vehicle.driverAssociation.b2BContractDriverId || vehicle.driverAssociation.B2BContractDriverId;
          if (associationDriverId && +associationDriverId === +driverId) {
            // Se estamos editando o mesmo veículo, permitir
            if (this.selectedVehicle && vehicle.id === this.selectedVehicle.id) {
              continue;
            }
            return { assigned: true, vehiclePlate: vehicle.plate };
          }
        }
      }
    }
    return { assigned: false };
  }

  onSubmitAssignment(): void {
    if (this.assignmentForm.invalid || !this.contractId || !this.selectedVehicle) {
      this.assignmentForm.markAllAsTouched();
      return;
    }

    const formValue = this.assignmentForm.value;
    const driverId = +formValue.driverId;

    // Validar se o condutor já está vinculado a outro veículo
    const driverCheck = this.isDriverAlreadyAssigned(driverId);
    if (driverCheck.assigned) {
      this.errorService.showErrorMessage(
        `Este condutor já está vinculado ao veículo ${driverCheck.vehiclePlate}. ` +
        `Um condutor não pode ser atribuído a mais de um veículo simultaneamente. ` +
        `Por favor, desvincule o condutor do veículo atual antes de atribuí-lo a outro.`
      );
      return;
    }

    this.isSubmitting = true;

    // Formatar data no formato esperado (2025-01-20T08:00:00Z)
    const formatDate = (dateString: string): string => {
      if (!dateString) return null as any;
      const date = new Date(dateString);
      // Garantir que a data seja enviada no formato correto (ISO 8601)
      return date.toISOString();
    };

    const assignmentData = {
      b2BContractItemId: this.selectedVehicle.itemId,
      b2BContractItemVehicleSnapshotId: this.selectedVehicle.id,
      b2BContractDriverId: driverId,
      startDate: formatDate(formValue.startDate),
      endDate: formValue.endDate ? formatDate(formValue.endDate) : null,
      status: formValue.status,
      notes: formValue.notes || null
    };

    this.contractsService.assignDriverToVehicle(
      this.contractId!,
      this.selectedVehicle.itemId,
      assignmentData
    ).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.errorService.showSuccessMessage('Condutor vinculado ao veículo com sucesso!');

        // Atualizar estado local do veículo para refletir imediatamente a vinculação
        const vehicleIndex = this.vehicles.findIndex(v => v.id === this.selectedVehicle!.id);
        if (vehicleIndex !== -1) {
          this.vehicles[vehicleIndex] = {
            ...this.vehicles[vehicleIndex],
            hasDriver: true,
            b2BContractDriverId: driverId
          };
        }
        this.applyFilters();

        this.closeAssignmentModal();
        this.vehicleStatsFallbackDone = false;
        this.loadItems();
        this.loadDrivers();
        this.loadVehicles();
      },
      error: (err) => {
        this.isSubmitting = false;
        // Verificar se o erro é sobre condutor já vinculado
        const errorMessage = err?.error?.message || err?.message || '';
        if (errorMessage.toLowerCase().includes('condutor') || 
            errorMessage.toLowerCase().includes('vinculado') ||
            errorMessage.toLowerCase().includes('já está')) {
          this.errorService.showErrorMessage(
            `Não foi possível vincular o condutor: ${errorMessage}. ` +
            `Um condutor não pode ser atribuído a mais de um veículo simultaneamente.`
          );
        } else {
          this.errorService.handleHttpError(err);
        }
      }
    });
  }

  /** Abre modal para informar o motivo e desvincular via PUT no snapshot do veículo. */
  removeAssignment(vehicle: any): void {
    if (!vehicle.hasDriver || !this.contractId || !vehicle.id) return;
    const itemId = Number(vehicle.itemId || 0);
    if (!itemId) {
      this.errorService.showErrorMessage('Não foi possível identificar o item do contrato para desvincular.');
      return;
    }
    this.vehicleToUnassign = vehicle;
    this.unassignForm.reset({ motivo: '', returnDate: null });
    this.unassignOutroForm.reset({ detalhe: '' });
    this.showUnassignOutroModal = false;
    this.showUnassignModal = true;
  }

  /** Chave aceita pela API: condutor, associação ou snapshot do veículo (nessa ordem). */
  private resolveDriverKey(vehicle: any): number {
    const candidates = [
      vehicle?.b2BContractDriverId,
      vehicle?.driverAssociation?.b2BContractDriverId,
      vehicle?.b2BContractItemDriverId,
      vehicle?.driverAssociation?.id,
      vehicle?.id
    ];
    for (const candidate of candidates) {
      const value = Number(candidate || 0);
      if (value > 0) return value;
    }
    return 0;
  }

  closeUnassignModal(): void {
    if (this.isSubmitting) return;
    this.showUnassignModal = false;
    this.showUnassignOutroModal = false;
    this.vehicleToUnassign = null;
    this.unassignForm.reset({ motivo: '', returnDate: null });
    this.unassignOutroForm.reset({ detalhe: '' });
  }

  closeUnassignOutroModal(): void {
    if (this.isSubmitting) return;
    this.showUnassignOutroModal = false;
    this.unassignOutroForm.reset({ detalhe: '' });
    this.showUnassignModal = true;
  }

  submitUnassign(): void {
    if (!this.contractId || !this.vehicleToUnassign) return;
    this.unassignForm.markAllAsTouched();
    const motivoCode = (this.unassignForm.get('motivo')?.value || '').toString().trim();
    if (!motivoCode || this.unassignForm.invalid) {
      this.errorService.showErrorMessage('Selecione o motivo da desvinculação.');
      return;
    }

    if (motivoCode === B2bDesvincularCondutorMotivo.Outro) {
      this.showUnassignModal = false;
      this.unassignOutroForm.reset({ detalhe: '' });
      this.showUnassignOutroModal = true;
      return;
    }

    this.executeUnassign(motivoCode);
  }

  submitUnassignOutro(): void {
    this.unassignOutroForm.markAllAsTouched();
    const detalhe = (this.unassignOutroForm.get('detalhe')?.value || '').toString().trim();
    const resolved = resolveDesvincularMotivoPayload(B2bDesvincularCondutorMotivo.Outro, detalhe);
    if (!resolved.ok) {
      this.errorService.showErrorMessage(resolved.error);
      return;
    }
    this.executeUnassign(resolved.motivo);
  }

  private executeUnassign(motivo: string): void {
    if (!this.contractId || !this.vehicleToUnassign) return;
    const itemId = Number(this.vehicleToUnassign.itemId || 0);
    const driverKey = this.resolveDriverKey(this.vehicleToUnassign);
    if (!itemId || !driverKey) {
      this.errorService.showErrorMessage('Dados do veículo incompletos para desvincular.');
      return;
    }
    const payload: { motivo: string; returnDate?: string } = { motivo };
    const rd = this.unassignForm.get('returnDate')?.value;
    if (rd != null && String(rd).trim() !== '') {
      payload.returnDate = formatB2bDevolucaoReturnDateUtc(String(rd));
    }
    this.isSubmitting = true;
    this.contractsService
      .unassignDriverFromVehicle(this.contractId, itemId, driverKey, payload)
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.errorService.showSuccessMessage('Condutor desvinculado do veículo com sucesso!');
          this.closeUnassignModal();
          this.vehicleStatsFallbackDone = false;
          this.loadItems();
          this.loadDrivers();
          this.loadVehicles();
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
  }

  navigateBack(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    } else {
      this.router.navigate(['/b2b/contracts']);
    }
  }

  formatCpf(value: string): string {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }
    return value;
  }

  formatPhone(value: string): string {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }
    return value;
  }

  getDriverName(driverId: number | string | null | undefined, fallbackName?: string | null): string {
    const fromFallback = this.normalizeDriverName(fallbackName);
    if (fromFallback) return fromFallback;
    if (driverId == null || driverId === '') return '';
    const id = Number(driverId);
    const driver = this.drivers.find(d => Number(d.id) === id)
      || this.modalDrivers.find(d => Number(d.id) === id);
    return this.normalizeDriverName(driver?.name) || '';
  }

  displayDriverName(vehicle: any): string {
    const name = this.getDriverName(
      vehicle?.b2BContractDriverId || vehicle?.driverAssociation?.b2BContractDriverId,
      vehicle?.driverName || vehicle?.driverAssociation?.driverName
    );
    if (name) return name;
    if (vehicle?.hasDriver) {
      return vehicle.driverNameLoading ? 'Carregando condutor...' : 'Condutor vinculado';
    }
    return 'Não informado';
  }

  getVehicleColorLabel(color: string | null | undefined): string {
    const value = (color || '').toString().trim();
    if (!value || value.toUpperCase() === 'N/A') {
      return 'não informada';
    }
    return value;
  }

  getAvailableVehiclesCount(): number {
    return this.availableVehiclesCount;
  }

  getAssignedVehiclesCount(): number {
    return this.assignedVehiclesCount;
  }

  private loadModalDrivers(search?: string | null): void {
    if (!this.contractId) return;
    this.isLoadingModalDrivers = true;
    const term = (search ?? this.modalDriverSearchTerm ?? '').trim();
    this.contractsService.getB2bContractDrivers(this.contractId, 1, 200, term || null).subscribe({
      next: (response) => {
        const driversData = Array.isArray(response?.data) ? response.data : [];
        let mapped = driversData.map((driver: any) => this.mapDriver(driver));
        if (term) {
          mapped = mapped.filter((driver: any) => this.matchesDriverSearch(driver, term));
        }
        this.modalDrivers = mapped;
        this.isLoadingModalDrivers = false;
      },
      error: () => {
        const fallback = this.drivers.length ? this.drivers : [];
        this.modalDrivers = term
          ? fallback.filter((driver: any) => this.matchesDriverSearch(driver, term))
          : fallback;
        this.isLoadingModalDrivers = false;
      }
    });
  }

  private mapDriver(driver: any): any {
    return {
      id: driver.Id || driver.id,
      name: driver.Name || driver.name || '',
      driverCpf: driver.DriverCpf || driver.driverCpf || '',
      driverCnh: driver.DriverCnh || driver.driverCnh || '',
      phone: driver.Phone || driver.phone || '',
      email: driver.Email || driver.email || '',
      itemAssociations: driver.ItemAssociations || driver.itemAssociations || []
    };
  }

  private mapVehicle(vehicle: any): any {
    const vehicleId = this.pickValue(vehicle, 'Id', 'id');
    const driverId = this.pickValue(vehicle, 'B2BContractDriverId', 'b2BContractDriverId', 'b2bContractDriverId');
    const driverName = this.extractDriverName(vehicle);
    const assignmentId = this.pickValue(vehicle, 'B2BContractItemDriverId', 'b2BContractItemDriverId', 'b2bContractItemDriverId');
    const hasDriver = this.resolveHasDriver(vehicle, driverId, assignmentId, driverName);
    const colorRaw = (this.pickValue(vehicle, 'Color', 'color') || '').toString().trim();

    return {
      id: vehicleId,
      plate: this.pickValue(vehicle, 'Plate', 'plate') || '—',
      model: this.pickValue(vehicle, 'Model', 'model') || '—',
      brand: this.pickValue(vehicle, 'Brand', 'brand') || '—',
      year: this.pickValue(vehicle, 'Year', 'year') || '—',
      color: colorRaw || null,
      itemId: this.pickValue(vehicle, 'B2BContractItemId', 'b2BContractItemId', 'b2bContractItemId'),
      itemNumber: this.pickValue(vehicle, 'ItemNumber', 'itemNumber') || '—',
      hasDriver,
      driverName,
      b2BContractDriverId: driverId,
      b2BContractItemDriverId: assignmentId,
      driverAssociation: assignmentId || driverId ? {
        id: assignmentId,
        b2BContractDriverId: driverId,
        driverName
      } : null,
      originalVehicleId: vehicleId
    };
  }

  private extractDriverName(source: any): string | null {
    if (!source) return null;
    const nested = source.driver || source.Driver || source.b2BContractDriver || source.B2BContractDriver;
    return this.normalizeDriverName(
      this.pickValue(source, 'DriverName', 'driverName', 'driver_name', 'Name', 'name')
      || this.pickValue(nested, 'Name', 'name', 'DriverName', 'driverName')
    );
  }

  private normalizeDriverName(value: any): string | null {
    if (value == null) return null;
    const text = String(value).trim();
    if (!text || text.toLowerCase() === 'não informado' || text.toLowerCase() === 'nao informado') {
      return null;
    }
    return text;
  }

  private resolveHasDriver(vehicle: any, driverId: any, assignmentId: any, driverName: string | null): boolean {
    const flag = vehicle?.HasDriver ?? vehicle?.hasDriver;
    if (flag === true || flag === 'true' || flag === 1 || flag === '1') {
      return true;
    }
    if (flag === false || flag === 'false' || flag === 0 || flag === '0') {
      return !!(driverId || assignmentId || driverName);
    }
    return !!(driverId || assignmentId || driverName);
  }

  private pickValue(source: any, ...keys: string[]): any {
    if (!source) return null;
    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return null;
  }
}
